import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useOrganisation } from "@/lib/OrganisationContext";
import { adminClient, formatAdminDate } from "@/api/adminClient";
import ActAsDialog from "@/components/ActAsDialog";
import MembersCard from "@/components/settings/MembersCard";
import { usePendingNavigationLock } from "./useDirtyNavigationGuard";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";

export default function AdminCustomers() {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [managed, setManaged] = useState(null);
  const [actAs, setActAs] = useState(null);
  const [actAsBusy, setActAsBusy] = useState(false);
  const [actAsError, setActAsError] = useState("");
  const [total, setTotal] = useState(0);
  const generation = useRef(0);
  const actAsPending = useRef(false);
  const mounted = useRef(true);
  const managedRef = useRef(null);
  const { beginActAs } = useOrganisation();
  const { query, update, reset } = useAdminListQuery();
  usePendingNavigationLock(actAsBusy);

  async function load() {
    const current = ++generation.current;
    setState("loading");
    setError("");
    try {
      const params = new URLSearchParams({ paged: "true", limit: "25" });
      for (const key of ["q", "status", "offset", "sort", "direction"]) if (query[key]) params.set(key, query[key]);
      const result = await adminClient(`/admin/customers?${params}`);
      if (current !== generation.current) return;
      setRows(result.items);
      setTotal(result.total);
      setState("ready");
    } catch (e) {
      if (current !== generation.current) return;
      setError(e.message);
      setState("error");
    }
  }

  useEffect(() => {
    load();
    return () => {
      generation.current += 1;
    };
  }, [query.q, query.status, query.offset, query.sort, query.direction]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function startActAs(reason) {
    if (actAsPending.current || !actAs) return;
    const target = actAs.id;
    const routeIntent = `${window.location.pathname}${window.location.search}`;
    actAsPending.current = true;
    setActAsBusy(true);
    setActAsError("");
    try {
      await beginActAs(target, reason, {
        shouldApply: () =>
          `${window.location.pathname}${window.location.search}` ===
          routeIntent,
      });
      if (mounted.current) setActAs(null);
    } catch (e) {
      if (mounted.current)
        setActAsError(e.message || "Could not start act-as session");
    } finally {
      actAsPending.current = false;
      if (mounted.current) setActAsBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Customers</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Review customer organisations and open their access controls.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/admin/customers/new"
            className="inline-flex min-h-11 items-center rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800"
          >
            New customer
          </Link>
          <button
            type="button"
            onClick={load}
            disabled={state === "loading"}
            className="min-h-11 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50"
          >
            {state === "loading" ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>
      <AdminListToolbar search={query.q || ""} searchLabel="Search organisation name or slug" total={total} loading={state === "loading"} onSearch={(q) => update({ q })} onReset={reset}>
        <label className="text-sm font-medium">Status<select aria-label="Filter customers by status" value={query.status || "all"} onChange={(event) => update({ status: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"><option value="all">All statuses</option><option value="active">Active</option><option value="suspended">Suspended</option><option value="archived">Archived</option></select></label>
      </AdminListToolbar>
      {state === "loading" && (
        <p
          role="status"
          className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground"
        >
          Loading customers…
        </p>
      )}
      {state === "error" && (
        <div
          className="rounded-lg border border-destructive/40 bg-card p-5"
          role="alert"
        >
          <p className="text-sm text-red-800">{error}</p>
          <button
            type="button"
            onClick={load}
            className="mt-3 min-h-11 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-muted"
          >
            Retry
          </button>
        </div>
      )}
      {state === "ready" &&
        (rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
            No customer organisations found.
          </p>
        ) : (
          <>
          <div
            role="region"
            aria-label="Customer directory table"
            tabIndex={0}
            className="relative overflow-x-auto rounded-xl border border-border bg-card shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          >
            <table className="w-full min-w-[760px] text-left text-sm">
              <caption className="sr-only">Customer organisations</caption>
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3">Organisation</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Level</th>
                  <th className="px-4 py-3">Members</th>
                  <th className="px-4 py-3">SSO</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-border last:border-0"
                  >
                    <th
                      scope="row"
                      className="px-4 py-3 font-semibold text-foreground"
                    >
                      <Link
                        className="underline decoration-teal-600 underline-offset-2"
                        to={`/admin/customers/${encodeURIComponent(row.id)}`}
                      >
                        {row.name}
                      </Link>
                    </th>
                    <td className="px-4 py-3">
                      <span className="rounded-full border border-border px-2 py-1 text-xs capitalize">
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">L{row.current_dc_level ?? 0}</td>
                    <td className="px-4 py-3">{row.members}</td>
                    <td className="px-4 py-3">{row.sso_providers ?? 0}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatAdminDate(row.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        {!row.is_provider && (
                          <>
                            <Link
                              to={`/admin/customers/${encodeURIComponent(row.id)}`}
                              className="inline-flex min-h-11 items-center rounded-lg border border-teal-700 px-3 py-2 text-xs font-semibold text-teal-800 hover:bg-teal-50"
                            >
                              View customer
                            </Link>
                            <button
                              type="button"
                              onClick={() => {
                                setManaged(row);
                                window.requestAnimationFrame(() =>
                                  managedRef.current?.scrollIntoView({
                                    behavior: "smooth",
                                    block: "start",
                                  }),
                                );
                              }}
                              className="min-h-11 rounded-lg border border-teal-700 px-3 py-2 text-xs font-semibold text-teal-800 hover:bg-teal-50"
                            >
                              Manage members
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setActAsError("");
                                setActAs(row);
                              }}
                              disabled={row.status !== "active"}
                              className="min-h-11 rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted disabled:opacity-50"
                            >
                              Act as
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <AdminListPager offset={Number(query.offset || 0)} limit={25} total={total} disabled={state === "loading"} onPage={(offset) => update({ offset }, { resetOffset: false })} />
          </>
        ))}
      {managed && (
        <section
          ref={managedRef}
          className="rounded-xl border border-teal-200 bg-teal-50/40 p-4"
          aria-labelledby="manage-members-title"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <h3
                id="manage-members-title"
                className="text-lg font-semibold text-foreground"
              >
                Manage members
              </h3>
              <p className="text-sm text-muted-foreground">
                Target: {managed.name}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setManaged(null)}
              className="min-h-11 rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold hover:bg-muted"
            >
              Close
            </button>
          </div>
          <MembersCard
            organisationId={managed.id}
            organisationName={managed.name}
            canManage
            canManageOwnership
            providerTarget
            readOnly={managed.status !== "active"}
          />
        </section>
      )}
      {actAs && (
        <ActAsDialog
          organisation={actAs}
          onSubmit={startActAs}
          onClose={() => setActAs(null)}
          busy={actAsBusy}
          error={actAsError}
        />
      )}
    </div>
  );
}
